// Shared reverse-geocoding helpers.
// Given a { lat, lon } (or common variants) location object, resolves a
// human-readable place name via Mappls/MapMyIndia, with in-memory caching so
// repeated coordinates don't refetch.

const locationNameCache = new Map();
const locationNameRequests = new Map();

export function getLocationCoords(locationValue) {
  if (!locationValue || typeof locationValue !== "object") return null;

  const lat = Number(locationValue.lat ?? locationValue.latitude);
  const lon = Number(locationValue.lon ?? locationValue.lng ?? locationValue.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lat, lon };
}

export function formatCoords(locationValue) {
  const coords = getLocationCoords(locationValue);
  if (!coords) return "-";
  return `${coords.lat}, ${coords.lon}`;
}

export function getLocationCacheKey(locationValue) {
  const coords = getLocationCoords(locationValue);
  return coords ? `${coords.lat.toFixed(6)},${coords.lon.toFixed(6)}` : null;
}

// General-purpose "make anything displayable" helper, mirroring the
// toText/toDisplayText helpers that used to be duplicated across pages.
// Returns null when the value is a bare coordinate object (lat/lon only),
// signalling that the caller should reverse-geocode instead.
export function toDisplayText(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => toDisplayText(item)).join(", ");
  if (typeof value === "object") {
    if (typeof value.address === "string" && value.address.trim()) return value.address;
    if (typeof value.place === "string" && value.place.trim()) return value.place;
    if (typeof value.name === "string" && value.name.trim()) return value.name;
    if (getLocationCoords(value)) return null; // signal: bare coordinates, needs geocoding
    return JSON.stringify(value);
  }
  return String(value);
}

function pickPlaceName(address = {}) {
  return (
    address.neighbourhood ||
    address.suburb ||
    address.village ||
    address.town ||
    address.city ||
    address.county ||
    address.state_district ||
    address.state ||
    address.country
  );
}

export function getCachedLocationName(locationValue) {
  const cacheKey = getLocationCacheKey(locationValue);
  return cacheKey ? locationNameCache.get(cacheKey) : undefined;
}

export async function reverseGeocodeLocation(locationValue) {
  const cacheKey = getLocationCacheKey(locationValue);
  if (!cacheKey) return null;

  if (locationNameCache.has(cacheKey)) {
    return locationNameCache.get(cacheKey);
  }

  if (locationNameRequests.has(cacheKey)) {
    return locationNameRequests.get(cacheKey);
  }

  const coords = getLocationCoords(locationValue);
  const sdkKey = process.env.REACT_APP_MAPPLS_MAP_SDK_KEY?.trim();
  if (!sdkKey) return null;

  const request = fetch(
    `https://apis.mappls.com/advancedmaps/v1/${encodeURIComponent(sdkKey)}/rev_geocode?lat=${encodeURIComponent(coords.lat)}&lng=${encodeURIComponent(coords.lon)}`,
    {
      headers: {
        Accept: "application/json",
      },
    },
  )
    .then(async (response) => {
      if (!response.ok) return null;
      const data = await response.json();
      return data?.results?.[0]?.formatted_address || data?.display_name || pickPlaceName(data?.address) || null;
    })
    .catch(() => null)
    .then((result) => {
      locationNameCache.set(cacheKey, result);
      locationNameRequests.delete(cacheKey);
      return result;
    });

  locationNameRequests.set(cacheKey, request);
  return request;
}
